import { CardContent, CardHeader, Stack } from "@mui/material";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import MaterialTypography from "../../../components/MaterialTypography";
import { useNavigate } from "react-router-dom";
import Table from "../../../components/Table";
import { useEffect, useState } from "react";
import { deleteData, fetchData, postData } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import { useSnackbar } from "../../../context/popup";
import UploadFile from "../../Utils/UploadFile";
import SchemasListFilterDialog from "./components/SchemasListFilterDialog";
import { useLoading } from "../../../components/Loading";
import { FilterAlt } from "@mui/icons-material";

const SchemasList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector((state) => state?.schemaStore?.currentPage);
  const rowsPerPage = useSelector((state) => state?.schemaStore?.rowsPerPage);
  const totalPages = useSelector((state) => state?.schemaStore?.totalPages);

  const [isRefresh, setIsRefresh] = useState(false);
  const [SchemasData, setSchemasData] = useState([]);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
    apiCategoryId: "",
    schemaTypeId: "",
    compatibilityModeId: "",
  });

  useEffect(() => {
    fetchSchemasList();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //List API
  const fetchSchemasList = async () => {
    setCircleLoading(true);
    try {
      let url = `/api-engine/schema?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;

      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      if (filterData.apiCategoryId !== "") {
        url += `&apiCategoryId=${filterData.apiCategoryId}`;
      }

      if (filterData.schemaTypeId !== "") {
        url += `&schemaTypeId=${filterData.schemaTypeId}`;
      }

      if (filterData.compatibilityModeId !== "") {
        url += `&compatibilityModeId=${filterData.compatibilityModeId}`;
      }

      const response = await fetchData(url);

      if (response?.success) {
        setSchemasData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      } else {
        //throw error
        showSnackbar("Error Fetching Schemas List", "error");
      }
    } catch (error) {
      console.error("Error Fetching Schemas:", error);
      showSnackbar(error?.response?.data?.message || "Error Fetching Schemas List", "error");
    } finally {
      setCircleLoading(false);
    }
  };

  //bulk file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true);
    setUploadSuccess(false);

    try {
      const formData = new FormData();

      formData.append("file", file);

      const result = await postData(`/api-engine/schema`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (result?.success) {
        setUploadSuccess(true);
        showSnackbar(result?.message, "success");
        setOpenUploadFileDialog(false);
        setClearFileOnSuccess(true);
        fetchSchemasList();
      } else {
        setUploadSuccess(false);
        showSnackbar("Faiiled to upload file", "error");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      console.error("Upload error:", error || errorMessage);
      showSnackbar(errorMessage || "Network Error", "error");
    } finally {
      setFileUploading(false);
    }
  };

  //delete api
  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/api-engine/schema-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      if (response?.success) {
        showSnackbar("Schemas deleted successfully", "success");
      } else {
        showSnackbar("Failed to delete Schemas", "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar(error?.response?.data?.message || "Error Deleting Schemas", "error");
    }
  };

  //Table Mapping
  const SchemasListTable = {
    columns: [
      {
        key: "sno",
        label: "S.No.",
        renderCell: (row) => row.sno,
      },
      {
        key: "name",
        label: "Name",
        renderCell: (row) => row.name,
      },
      {
        key: "gProductId",
        label: "General Product",
        renderCell: (row) => row.gProductId,
      },
      {
        key: "apiCategoryId",
        label: "API Category",
        renderCell: (row) => row.apiCategoryId,
      },
      {
        key: "schemaTypeId",
        label: "Schema Type",
        renderCell: (row) => row.schemaTypeId ?? "-",
      },
      {
        key: "jsonSchema",
        label: "JSON Schema",
        isObject: true,
      },
      {
        key: "effectiveFrom",
        label: "Effective From",
        renderCell: (row) => row.effectiveFrom,
      },
      {
        key: "effectiveTo",
        label: "Effective To",
        renderCell: (row) => row.effectiveTo,
      },
      {
        key: "isActive",
        label: "Status",
        renderCell: (row) => (
          <span
            style={{
              color: row.isActive ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {row.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],

    rows: SchemasData?.map((schema, index) => ({
      id: schema.id,
      sno: index + 1,

      name: schema.name,

      //  user-friendly labels
      gProductId: schema.product?.keyName ?? "-",
      apiCategoryId: schema.apiCategory?.keyName ?? "-",
      schemaTypeId: schema.schemaType?.keyName ?? "-", // if exists

      version: schema.version,

      //  stringify for display
      jsonSchema: schema.jsonSchema ?? {},

      effectiveFrom: schema.effectiveFrom
        ? new Date(schema.effectiveFrom).toLocaleDateString()
        : "-",
      effectiveTo: schema.effectiveTo
        ? new Date(schema.effectiveTo).toLocaleDateString()
        : "-",

      isActive: schema.isActive,
    })),
  };

  const handleAddNewSchemas = () => {
    navigate(`/api-integration-engine/schemas/schemas-details`);
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,

    onEdit: (row) => {
      navigate(
        `/api-integration-engine/schemas/schemas-details?pageType=edit&id=${row.id}`
      );
    },
    onView: (row) => {
      navigate(
        `/api-integration-engine/schemas/schemas-details?pageType=view&id=${row.id}`
      );
    },
    onDelete: (row) => handleDelete(row),
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Schemas List
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of Schemas Details according to Schema Type.
              </MaterialTypography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewSchemas()}
              >
                Schemas
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <CardContent>
        <Table
          columns={SchemasListTable?.columns}
          data={SchemasListTable?.rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel="No Schemas found"
          handleSearch={handleSearch}
        />
      </CardContent>
      <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={["csv", "xlsx"]}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
        clearFileOnSuccess={clearFileOnSuccess}
      />
      <SchemasListFilterDialog
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchSchemasList(); // call API after filter applied
        }}
      />
    </DashboardLayout>
  );
};

export default SchemasList;
